import * as appointments from '@seen/core/models/appointments/index'
import type { Order } from '@seen/core/models/orders/Order'
import { getReferenceString } from '@seen/core/references'
import { useQueries } from '@tanstack/react-query'
import { chunk, sortBy } from 'lodash-es'
import { useMemo } from 'react'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export function useOrderAppointmentsMap(
  orders: Order[]
): Map<string, appointments.ExtendedAppointment | undefined> {
  const orderChunks = useMemo(() => chunk(orders, 250), [orders])
  const medplum = useMedplum()

  const queries = useQueries({
    queries: orderChunks.map((chunk) => ({
      queryKey: ['orders', 'appointments', chunk.map((order) => order.id)],
      queryFn: () =>
        medplum.searchResourceClassPagesAll(appointments.ExtendedAppointment, {
          ...appointments.ExtendedAppointment.byBasedOnList(
            chunk.map((o) => o.reference)
          ),
          status: 'booked',
        }),
    })),
  })

  const dataKey = queries.map((q) => q.dataUpdatedAt).join(',')

  const appointmentList = useMemo(
    () => queries.flatMap((q) => q.data ?? []),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [dataKey]
  )

  const orderAppointmentsMap = useMemo(() => {
    const map = new Map<string, appointments.ExtendedAppointment[]>()
    appointmentList.forEach((appt) => {
      for (const basedOn of appt.basedOn ?? []) {
        map.set(getReferenceString(basedOn), [
          ...(map.get(getReferenceString(basedOn)) ?? []),
          appt,
        ])
      }
    })
    return map
  }, [appointmentList])

  const orderAppointmentMap = useMemo(() => {
    const map = new Map<string, appointments.ExtendedAppointment | undefined>()
    for (const [refString, appts] of orderAppointmentsMap.entries()) {
      map.set(refString, getNextAppointment(appts) ?? undefined)
    }
    return map
  }, [orderAppointmentsMap])

  return orderAppointmentMap
}

function getNextAppointment(
  appointments: appointments.ExtendedAppointment[]
): appointments.ExtendedAppointment | undefined {
  const futureBookedAppointments = appointments.filter((appt) => !appt.isPast)
  return sortBy(futureBookedAppointments, 'start')[0] ?? appointments[0]
}
